/**
 * The generated paper, rendered as a paper (spec §12).
 *
 * Presentation only — no fetching, no state beyond the mark-scheme toggle — so it
 * renders identically against the generation service and against a fixture.
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import type { ReactNode } from "react";
import type { Paper, PaperQuestion } from "./types";
import { questionMarks, reconcile, shortSchemeLine, shortSchemes } from "./marks";
import { MarkSchemeSheet } from "./MarkSchemeSheet";
import { PrintMenu, type PrintMode } from "./PrintMenu";
import { AnswerSheets } from "./AnswerSheets";
import { CoverTag, PrintPages, identityLine, type PaperIdentity, type SheetKind } from "./PrintTag";
import { RichText } from "../text/RichText";
import "../styles/paper.css";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

const DISCLAIMER =
  "Generated practice material. It is written to match the style of the exam board\u2019s papers, but it is not a past paper and is not endorsed by any exam board.";

/** Ruled lines to write on. Extended answers get more room, as on a real paper. */
function answerLineCount(question: PaperQuestion, marks: number): number {
  if (question.type === "multiple_choice") return 0;
  if (question.type === "extended") return Math.max(6, marks * 2);
  return Math.max(2, marks);
}

function AnswerLines({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <div className="answer-lines" aria-hidden="true" data-testid="answer-lines">
      {Array.from({ length: count }, (_, i) => (
        <div className="answer-lines__line" key={i} />
      ))}
    </div>
  );
}

/** The model answer, then one row per marking point, each worth one mark. More points than
 * marks reads as real schemes print it, "Any 3 from:". A point's mark is shown only when every
 * mark has a point: a shortfall is the paper's own notice, and a "1" beside each point would
 * be a claim nobody checked. */
function MarkScheme({
  answer,
  points,
  marks,
}: {
  answer: string;
  points: string[];
  marks: number;
}) {
  const oneEach = marks > 0 && points.length >= marks;
  const anyOf = oneEach && points.length > marks;
  return (
    <div className="scheme">
      <div className="scheme__heading">Mark scheme</div>
      {answer && (
        <div className="scheme__answer">
          <RichText text={answer} block />
        </div>
      )}
      {anyOf && <div className="scheme__any">Any {marks} from:</div>}
      {points.length > 0 && (
        <ul className={`scheme__points${oneEach ? " scheme__points--marked" : ""}`}>
          {points.map((point, i) => (
            <li key={`${i}-${point}`}>
              <span>
                <RichText text={point} />
              </span>
              {oneEach && <span className="scheme__mark">1</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KIND: Record<PrintMode, SheetKind> = {
  paper: "Question paper",
  scheme: "Mark scheme",
  answers: "Paper with answers",
  sheets: "Answer sheet",
  both: "Paper and mark scheme",
};

const two = (n: number | string) => String(n).padStart(2, "0");
const markWord = (n: number) => `[${n} ${n === 1 ? "mark" : "marks"}]`;

/** One grid per question: number, body, and the mark column, which sizes to its content —
 * a fixed width wraps "[3 marks]". Parts are rows of the same grid, so their marks line up. */
function Question({ question, showScheme }: { question: PaperQuestion; showScheme: boolean }) {
  const marks = questionMarks(question);
  const hasParts = question.parts.length > 0;

  return (
    <article className="question" aria-label={`Question ${question.number}`}>
      <div className="question__number">{two(question.number)}</div>

      <div className="question__body">
        {question.topic && <div className="question__topic">{question.topic}</div>}
        <div className="question__stem">
          <RichText text={question.stem} block />
        </div>

        {question.type === "multiple_choice" && question.options.length > 0 && (
          <ul className="options">
            {question.options.map((option, i) => (
              <li className="options__item" key={option}>
                <span className="options__lozenge" />
                <span className="options__letter">{LETTERS[i]}</span>
                <span>
                  <RichText text={option} />
                </span>
              </li>
            ))}
          </ul>
        )}

        {!hasParts && <AnswerLines count={answerLineCount(question, marks)} />}
        {!hasParts && showScheme && (
          <MarkScheme answer={question.answer} points={question.marking_points} marks={marks} />
        )}
      </div>

      <div className="question__marks">{hasParts ? "" : markWord(marks)}</div>

      {question.parts.map((part) => (
        <Fragment key={part.label}>
          <div />
          <div className="part">
            <div className="part__label">{part.label}</div>
            <div>
              <div className="part__text">
                <RichText text={part.text} block />
              </div>
              <AnswerLines count={answerLineCount(question, part.marks)} />
              {showScheme && (
                <MarkScheme answer={part.answer} points={part.marking_points} marks={part.marks} />
              )}
            </div>
          </div>
          <div className="question__marks question__marks--part">[{part.marks}]</div>
        </Fragment>
      ))}
    </article>
  );
}

export function PaperView({
  paper,
  board,
  disclaimer,
  headingLevel = 1,
  back,
  notices,
  aside,
  identity,
}: {
  paper: Paper;
  /** Paper N, its Ref and its subject: on the toolbar, the cover and every printed page. */
  identity?: PaperIdentity;
  /** The toolbar's left end: the way back to the subject. */
  back?: ReactNode;
  /** The screen's own notices, set on the sheet under the cover. */
  notices?: ReactNode;
  /** Beside the sheet on a desk, below it on a phone; never printed. */
  aside?: ReactNode;
  board?: string;
  /** The service's own words for a generated paper; the fixed notice is the fixture's. */
  disclaimer?: string;
  /** 2 when this surface is embedded in a page that already owns the <h1>. */
  headingLevel?: 1 | 2;
}) {
  const Title = `h${headingLevel}` as "h1" | "h2";
  const [showScheme, setShowScheme] = useState(false);
  // Which document a Print choice asked for, until the dialog closes. Without one (the
  // browser's own print), the paper prints as it is shown.
  const [printMode, setPrintMode] = useState<PrintMode | null>(null);
  useEffect(() => {
    const reset = () => setPrintMode(null);
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, []);
  const printAs = (mode: PrintMode) => {
    // Rendered before the dialog snapshots the page: "with answers" needs the schemes drawn.
    flushSync(() => setPrintMode(mode));
    window.print();
  };
  const printing = printMode ?? (showScheme ? "answers" : "paper");
  const schemeDrawn = showScheme || printMode === "answers";

  // Reconciliation is cheap and deterministic; recompute only when the paper changes.
  const marks = useMemo(() => reconcile(paper), [paper]);
  const short = useMemo(() => paper.questions.flatMap(shortSchemes), [paper.questions]);

  return (
    <div className="paper-desk" data-print={printing}>
      <div className="toolbar" role="toolbar" aria-label="Paper">
        {back}
        {identity && <span className="toolbar__title">{identityLine(identity)}</span>}
        <span className="toolbar__spacer" />
        {/* The name stays "Show mark scheme" (spec, accessible names); the switch shows state. */}
        <button
          type="button"
          className={`button switch${showScheme ? " switch--on" : ""}`}
          aria-label="Show mark scheme"
          aria-pressed={showScheme}
          onClick={() => setShowScheme((on) => !on)}
        >
          <span className="switch__track" aria-hidden="true" />
          Mark scheme
        </button>
        <PrintMenu onPrint={printAs} />
      </div>

      <div className="paper-desk__body">
        <main className={`paper${showScheme ? " paper--scheme" : ""}`}>
          <header className="cover">
            {identity && (
              <CoverTag
                identity={identity}
                kind={printing === "answers" ? "Paper with answers" : "Question paper"}
              />
            )}
            <div className="cover__board">{board ?? "Practice paper"}</div>
            <Title className="cover__title">{paper.title}</Title>
            {identity?.name && <div className="cover__name">{identity.name}</div>}
            {identity && (
              <div className="cover__ref">
                Paper {identity.number} · Ref {identity.ref}
                {identity.made && ` · made ${identity.made}`}
              </div>
            )}
            <div className="cover__meta">
              <span>
                Time allowed: <strong>{paper.duration_minutes} minutes</strong>
              </span>
              <span>
                Total marks: <strong>{paper.total_marks}</strong>
              </span>
              <span>
                Questions: <strong>{paper.questions.length}</strong>
              </span>
            </div>

            <div className="candidate-box">
              <div className="candidate-box__field">
                <div className="candidate-box__label">Name</div>
              </div>
              <div className="candidate-box__field">
                <div className="candidate-box__label">Date</div>
              </div>
              {identity && (
                <p className="candidate-box__separate">
                  If you answer on separate sheets, write{" "}
                  <strong>
                    Paper {identity.number} · {identity.ref}
                  </strong>{" "}
                  at the top of each one.
                </p>
              )}
            </div>

            {paper.instructions.length > 0 && (
              <ul className="instructions">
                {paper.instructions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </header>

          {notices}

          {/* Honesty as a brand trait (spec §12.7): a paper whose marks do not add
              up says so, rather than being quietly rendered as if it were sound. This is
              the last-line guard: generation corrects sums before the paper is stored. */}
          {!marks.ok && (
            <div className="notice notice--warn" role="alert">
              <strong className="notice__title">This paper does not reconcile</strong>
              {marks.discrepancies.map((d) => (
                <div key={d.where}>
                  {d.where}: states {d.expected}, questions total {d.actual}.
                </div>
              ))}
              <p className="notice__next">You can still use it for practice.</p>
            </div>
          )}

          {short.length > 0 && (
            <div className="notice notice--warn" role="status">
              <strong className="notice__title">Some answers are missing a marking point</strong>
              {short.map((s) => (
                <div key={s.where}>{shortSchemeLine(s)}</div>
              ))}
              <p className="notice__next">Use your judgement for the missing marks.</p>
            </div>
          )}

          <div className="paper__questions">
            {paper.questions.map((question) => (
              <Question key={question.number} question={question} showScheme={schemeDrawn} />
            ))}
          </div>

          <p className="paper__disclaimer">{disclaimer ?? DISCLAIMER}</p>
        </main>

        {(printMode === "scheme" || printMode === "both") && (
          <MarkSchemeSheet
            paper={paper}
            board={board}
            disclaimer={disclaimer ?? DISCLAIMER}
            title={Title}
            identity={identity}
          />
        )}

        {printMode === "sheets" && identity && <AnswerSheets paper={paper} identity={identity} />}
        {identity && <PrintPages identity={identity} kind={KIND[printing]} />}

        {aside && (
          <aside className="paper-aside" aria-label="About this paper">
            {aside}
          </aside>
        )}
      </div>
    </div>
  );
}
